/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray/Control types require any for array field names */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { lessonApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type {
  Lesson,
  LessonRequest,
  LessonCategory,
  LessonType,
  LessonPriority,
  LessonSkillLevel,
  DeliveryFrequency,
  DeliveryChannel,
  GenerationSource,
  LessonMediaType,
} from '@/types/learningManagement';

const lessonSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  skillId: z.string().optional(),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional(),
  content: z.object({
    headline: z.string().optional(),
    text: z.string().optional(),
    mediaUrl: z.string().optional(),
    mediaType: z.enum(['IMAGE', 'VIDEO', 'TEXT', 'AUDIO', 'DOCUMENT', 'OTHER']).optional(),
  }), // Required by API
  category: z.enum(['REMINDER', 'MOTIVATION', 'HABIT_BUILDING', 'PROGRESS_CHECK', 'GOAL_ALIGNMENT', 'SKILL_DEVELOPMENT', 'LEARNING_PATH', 'ASSESSMENT_PREP', 'REFLECTION', 'CELEBRATION']),
  lessonType: z.enum(['TECHNICAL', 'SOFT_SKILL', 'BEHAVIORAL', 'PRODUCTIVITY', 'WELLNESS', 'MOTIVATIONAL', 'CUSTOM']),
  priority: z.enum(['LOW', 'MEDIUM', 'HIGH', 'URGENT']),
  status: z.enum(['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED', 'EXPIRED', 'ARCHIVED']).optional(),
  targetAudiences: z.array(z.string()),
  followupPrompts: z.array(z.string()),
  skillLevel: z.enum(['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY']).optional(),
  deliveryFrequency: z.enum(['ONCE', 'DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'ON_TRIGGER', 'CUSTOM']).optional(),
  deliveryChannels: z.array(z.string()),
  activationDate: z.string().optional(),
  expirationDate: z.string().optional(),
  personalizationParams: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string()).optional(),
  lessonGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

type LessonFormData = z.infer<typeof lessonSchema>;

const CATEGORIES: LessonCategory[] = ['REMINDER', 'MOTIVATION', 'HABIT_BUILDING', 'PROGRESS_CHECK', 'GOAL_ALIGNMENT', 'SKILL_DEVELOPMENT', 'LEARNING_PATH', 'ASSESSMENT_PREP', 'REFLECTION', 'CELEBRATION'];
const LESSON_TYPES: LessonType[] = ['TECHNICAL', 'SOFT_SKILL', 'BEHAVIORAL', 'PRODUCTIVITY', 'WELLNESS', 'MOTIVATIONAL', 'CUSTOM'];
const PRIORITIES: LessonPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const SKILL_LEVELS: LessonSkillLevel[] = ['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY'];
const DELIVERY_FREQUENCIES: DeliveryFrequency[] = ['ONCE', 'DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'ON_TRIGGER', 'CUSTOM'];
const DELIVERY_CHANNELS: DeliveryChannel[] = ['IN_APP_NOTIFICATION', 'EMAIL', 'SMS', 'PUSH_NOTIFICATION', 'DASHBOARD', 'SLACK', 'TEAMS'];
const TARGET_AUDIENCES = ['ALL_USERS', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'SPECIFIC_ROLE', 'CUSTOM_SEGMENT'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];
const MEDIA_TYPES: LessonMediaType[] = ['IMAGE', 'VIDEO', 'TEXT', 'AUDIO', 'DOCUMENT', 'OTHER'];

export const LessonFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Array<{ id?: string; name: string }>>([]);

  const form = useForm<LessonFormData>({
    resolver: zodResolver(lessonSchema) as any,
    defaultValues: {
      title: '',
      skillId: '',
      description: '',
      content: {
        headline: '',
        text: '',
        mediaUrl: '',
        mediaType: 'TEXT',
      },
      category: 'REMINDER',
      lessonType: 'TECHNICAL',
      priority: 'MEDIUM',
      status: 'DRAFT',
      targetAudiences: [],
      followupPrompts: [],
      skillLevel: 'BASIC',
      deliveryFrequency: 'ONCE',
      deliveryChannels: [],
      activationDate: '',
      expirationDate: '',
      tags: [],
      lessonGenerated: 'USER_CUSTOMIZED',
    },
  });

  const { fields: audienceFields, append: appendAudience, remove: removeAudience } = useFieldArray({
    control: form.control as any,
    name: 'targetAudiences' as any,
  });

  const { fields: promptFields, append: appendPrompt, remove: removePrompt } = useFieldArray({
    control: form.control as any,
    name: 'followupPrompts' as any,
  });

  const { fields: channelFields, append: appendChannel, remove: removeChannel } = useFieldArray({
    control: form.control as any,
    name: 'deliveryChannels' as any,
  });

  useEffect(() => {
    skillApi.list({ page: 0, size: 1000 }).then((response) => {
      setSkills(response.content || []);
    }).catch((error) => {
      console.error('Error loading skills:', error);
    });
  }, []);

  // Fetch lesson data for edit mode
  const {
    data: _lesson,
    isLoading: isLoadingLesson,
    error: lessonError,
  } = useEntity<Lesson>({
    fetchFn: (lessonId: string) => lessonApi.getById(lessonId),
    entityId: id,
    entityName: 'lesson',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        skillId: data.skillId || '',
        description: data.description || '',
        content: data.content || {
          headline: '',
          text: '',
          mediaUrl: '',
          mediaType: 'TEXT',
        },
        category: data.category,
        lessonType: data.lessonType,
        priority: data.priority,
        targetAudiences: data.targetAudiences || [],
        followupPrompts: data.followupPrompts || [],
        skillLevel: data.skillLevel,
        deliveryFrequency: data.deliveryFrequency,
        deliveryChannels: data.deliveryChannels || [],
        activationDate: data.activationDate ? new Date(data.activationDate).toISOString().split('T')[0] : '',
        expirationDate: data.expirationDate ? new Date(data.expirationDate).toISOString().split('T')[0] : '',
        tags: data.tags || [],
        lessonGenerated: data.lessonGenerated,
        status: data.status,
      } as any);
    },
    onError: () => {
      navigate('/admin/lessons');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: LessonFormData) => {
      const payload: LessonRequest = {
        ...data,
        skillId: data.skillId === 'none' || !data.skillId ? undefined : data.skillId,
        status: data.status || 'DRAFT',
        targetAudiences: data.targetAudiences || [],
        followupPrompts: data.followupPrompts || [],
        deliveryChannels: data.deliveryChannels || [],
        // Convert date to ISO date-time format
        activationDate: data.activationDate ? new Date(data.activationDate + 'T00:00:00').toISOString() : undefined,
        expirationDate: data.expirationDate ? new Date(data.expirationDate + 'T00:00:00').toISOString() : undefined,
      } as LessonRequest;
      return await lessonApi.create(payload);
    },
    entityName: 'lesson',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/lessons');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: LessonFormData) => {
      if (!id) throw new Error('Lesson ID is required');
      const payload: LessonRequest = {
        ...data,
        skillId: data.skillId === 'none' || !data.skillId ? undefined : data.skillId,
        status: data.status || 'DRAFT',
        targetAudiences: data.targetAudiences || [],
        followupPrompts: data.followupPrompts || [],
        deliveryChannels: data.deliveryChannels || [],
        // Convert date to ISO date-time format
        activationDate: data.activationDate ? new Date(data.activationDate + 'T00:00:00').toISOString() : undefined,
        expirationDate: data.expirationDate ? new Date(data.expirationDate + 'T00:00:00').toISOString() : undefined,
      } as LessonRequest;
      return await lessonApi.update(id, payload);
    },
    entityName: 'lesson',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/lessons');
    },
  });

  const onSubmit = async (data: LessonFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingLesson;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading lesson..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && lessonError) {
    return <ErrorState error={lessonError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/lessons">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Lesson' : 'Create Lesson'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update lesson information' : 'Create a new lesson'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
          console.error('Form validation errors:', errors);
        })} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the lesson</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter lesson title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter lesson description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="skillId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Skill</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(value === 'none' ? undefined : value)} 
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select skill (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {skills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id || ''}>
                            {skill.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Select the skill this lesson is associated with</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lessonType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lesson Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select lesson type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {LESSON_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PRIORITIES.map((priority) => (
                            <SelectItem key={priority} value={priority}>
                              {priority}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="skillLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Skill Level</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select skill level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SKILL_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="DRAFT">Draft</SelectItem>
                          <SelectItem value="ACTIVE">Active</SelectItem>
                          <SelectItem value="PAUSED">Paused</SelectItem>
                          <SelectItem value="COMPLETED">Completed</SelectItem>
                          <SelectItem value="EXPIRED">Expired</SelectItem>
                          <SelectItem value="ARCHIVED">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="deliveryFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Frequency</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DELIVERY_FREQUENCIES.map((freq) => (
                            <SelectItem key={freq} value={freq}>
                              {freq.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="activationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Activation Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="expirationDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiration Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Content Section */}
              <div className="space-y-4 border-t pt-4">
                <Label>Content *</Label>
                <FormField
                  control={form.control}
                  name="content.headline"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Headline</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter headline" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="content.text"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Text</FormLabel>
                      <FormControl>
                        <textarea
                          className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          placeholder="Enter lesson text content"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="content.mediaUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Media URL</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter media URL" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="content.mediaType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Media Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select media type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {MEDIA_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Target Audiences */}
              <div className="space-y-2">
                <Label>Target Audiences</Label>
                <div className="space-y-2">
                  {audienceFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Select
                        value={form.watch(`targetAudiences.${index}`)}
                        onValueChange={(value) => {
                          const current = form.getValues('targetAudiences') || [];
                          current[index] = value;
                          form.setValue('targetAudiences', current);
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select target audience" />
                        </SelectTrigger>
                        <SelectContent>
                          {TARGET_AUDIENCES.map((audience) => (
                            <SelectItem key={audience} value={audience}>
                              {audience.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={() => removeAudience(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendAudience('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Target Audience
                  </Button>
                </div>
              </div>

              {/* Follow-up Prompts */}
              <div className="space-y-2">
                <Label>Follow-up Prompts</Label>
                <div className="space-y-2">
                  {promptFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Input
                        placeholder="Enter follow-up prompt"
                        value={form.watch(`followupPrompts.${index}`) || ''}
                        onChange={(e) => {
                          const current = form.getValues('followupPrompts') || [];
                          current[index] = e.target.value;
                          form.setValue('followupPrompts', current);
                        }}
                      />
                      <Button type="button" variant="outline" size="icon" onClick={() => removePrompt(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendPrompt('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Follow-up Prompt
                  </Button>
                </div>
              </div>

              {/* Delivery Channels */}
              <div className="space-y-2">
                <Label>Delivery Channels</Label>
                <div className="space-y-2">
                  {channelFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Select
                        value={form.watch(`deliveryChannels.${index}`)}
                        onValueChange={(value) => {
                          const current = form.getValues('deliveryChannels') || [];
                          current[index] = value;
                          form.setValue('deliveryChannels', current);
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select delivery channel" />
                        </SelectTrigger>
                        <SelectContent>
                          {DELIVERY_CHANNELS.map((channel) => (
                            <SelectItem key={channel} value={channel}>
                              {channel.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={() => removeChannel(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendChannel('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Delivery Channel
                  </Button>
                </div>
              </div>

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter tags separated by commas"
                        value={field.value?.join(', ') || ''}
                        onChange={(e) => {
                          const tags = e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean);
                          field.onChange(tags);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lessonGenerated"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generation Source *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select generation source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GENERATION_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/lessons">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default LessonFormPage;







